import React, { useEffect, useState, useCallback } from "react";
import { Button, Input } from "../../../components/ui";
import { InviteService } from "../../../services/inviteService";
import type { InviteUsersResult, PendingInvitation } from "../../../types/invite";

interface CampaignInvitePanelProps {
  campaignId: number;
  onChanged?: () => void;
}

export const CampaignInvitePanel: React.FC<CampaignInvitePanelProps> = ({
  campaignId,
  onChanged,
}) => {
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<InviteUsersResult | null>(null);
  const [pending, setPending] = useState<PendingInvitation[]>([]);

  const loadPending = useCallback(async () => {
    try {
      setPending(await InviteService.listCampaignInvites(campaignId));
    } catch {
      setPending([]);
    }
  }, [campaignId]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const handleInvite = async () => {
    const name = username.trim();
    if (!name) return;
    setSubmitting(true);
    try {
      const res = await InviteService.inviteUsers(campaignId, [name]);
      setResult(res);
      setUsername("");
      await loadPending();
      onChanged?.();
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (inviteId: number) => {
    await InviteService.revokeInvite(campaignId, inviteId);
    await loadPending();
    onChanged?.();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            data-testid="invite-username-input"
          />
        </div>
        <Button
          variant="primary"
          onClick={handleInvite}
          disabled={submitting || !username.trim()}
          loading={submitting}
          data-testid="invite-submit"
        >
          {submitting ? "Sending..." : "Send Invites"}
        </Button>
      </div>

      {result && (
        <div className="space-y-2 text-sm">
          {result.invited.length > 0 && (
            <div className="bg-green-900/40 border border-green-700 rounded p-3">
              <span className="text-green-400 font-medium">Invited: </span>
              <span className="text-green-300">{result.invited.join(", ")}</span>
            </div>
          )}
          {result.alreadyMember.length > 0 && (
            <div className="bg-yellow-900/40 border border-yellow-700 rounded p-3">
              <span className="text-yellow-400 font-medium">Already members: </span>
              <span className="text-yellow-300">{result.alreadyMember.join(", ")}</span>
            </div>
          )}
          {result.alreadyInvited.length > 0 && (
            <div className="bg-amber-900/40 border border-amber-700 rounded p-3">
              <span className="text-amber-400 font-medium">Already invited: </span>
              <span className="text-amber-300">{result.alreadyInvited.join(", ")}</span>
            </div>
          )}
          {result.notFound.length > 0 && (
            <div className="bg-red-900/40 border border-red-700 rounded p-3">
              <span className="text-red-400 font-medium">Not found: </span>
              <span className="text-red-300">{result.notFound.join(", ")}</span>
            </div>
          )}
        </div>
      )}

      {pending.length > 0 && (
        <div>
          <h4 className="text-zinc-400 text-xs uppercase tracking-wide mb-2">
            Pending invitations
          </h4>
          <ul className="space-y-2">
            {pending.map((p) => (
              <li
                key={p.id}
                data-testid={`pending-invite-${p.id}`}
                className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm"
              >
                <span className="text-zinc-200">{p.invitedUsername ?? `Invite #${p.id}`}</span>
                <Button
                  variant="danger"
                  size="sm"
                  data-testid={`revoke-invite-${p.id}`}
                  onClick={() => handleRevoke(p.id)}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CampaignInvitePanel;
